<template>
    <load
        v-if="load"
        color="#f0f8ff"
        mode="table"
        message="Carregando conversa"
        style="height: 100% !important"
    />
    <div v-else class="chat-conversation-layout">
        <div ref="messageList" id="msn-list" class="rounded chat-message-list">
            <MessagesFeedGroup
                v-if="type == 'grupos'"
                :contact="contact"
                :messages="messages"
                :type="type"
            />
            <MessagesFeed
                v-else
                :contact="contact"
                :messages="messages"
                :loadmessage="loadmessage"
                :writemessage="writemessage"
                :type="type"
                :box="box"
            />
        </div>

        <div v-if="error" class="alert alert-danger mt-2 mb-1" role="alert">
            {{ error }}
            <div v-if="failedMessage" class="mt-1">
                <small v-if="failedMessage.attachment">Arquivo: {{ failedMessage.attachment.name }}</small>
                <button type="button" class="btn btn-outline-danger btn-sm ml-2" :disabled="loading" @click="retryMessage">
                    Tentar enviar novamente
                </button>
            </div>
        </div>

        <fieldset v-if="contact" class="chat-composer-area" :disabled="loading">
            <ChatAttachmentBar
                :key="conversationKey"
                v-model="attachment"
                :busy="loading"
                @send="sendAttachment"
            />
            <!-- Mantém o MessageComposer existente e seu contrato de eventos. -->
            <MessageComposer
                v-show="!loading"
                @send="sendMessage"
                @writing="$emit('writing')"
                @stop-writing="$emit('stop-writing')"
            />
            <small v-if="loading" class="text-muted d-block py-2" role="status">Enviando mensagem…</small>
        </fieldset>
    </div>
</template>

<script>
import MessagesFeed from './MessagesFeed';
import MessagesFeedGroup from './MessagesFeedGroup';
import MessageComposer from './MessageComposer';
import ChatAttachmentBar from './ChatAttachmentBar';
import { CHAT_CONVERSATION_URL, requestErrorMessage, validateAttachment } from './chatAttachments';

export default {
    props: {
        contact: { type: Object, default: null },
        messages: { type: Array, default: () => [] },
        load: { type: Boolean, default: false },
        type: { type: String, default: 'contatos' },
        box: { type: String, default: 'chat' },
        writemessage: { type: Boolean, default: false },
    },
    components: { MessagesFeed, MessagesFeedGroup, MessageComposer, ChatAttachmentBar },
    data() {
        return {
            loading: false,
            loadmessage: false,
            attachment: null,
            error: '',
            failedMessage: null,
            conversationVersion: 0,
        };
    },
    computed: {
        conversationKey() {
            return this.contact ? `${this.type}:${this.contact.id}` : '';
        },
    },
    watch: {
        conversationKey() {
            // A troca de conversa descarta o rascunho de anexo local.
            // A versão também protege o caso A -> B -> A durante um envio em andamento.
            this.conversationVersion += 1;
            this.attachment = null;
            this.error = '';
            this.failedMessage = null;
            this.scrollToBottom();
        },
        messages() {
            this.scrollToBottom();
        },
        load(value) {
            if (!value) {
                this.scrollToBottom();
            }
        },
    },
    mounted() {
        this.scrollToBottom();
    },
    methods: {
        sendMessage(text) {
            return this.submitMessage(typeof text === 'string' ? text : '', this.attachment);
        },
        sendAttachment() {
            if (!this.attachment) {
                return Promise.resolve(false);
            }

            return this.submitMessage('', this.attachment);
        },
        retryMessage() {
            if (!this.failedMessage || this.failedMessage.key !== this.conversationKey) {
                return Promise.resolve(false);
            }

            return this.submitMessage(this.failedMessage.text, this.failedMessage.attachment);
        },
        async submitMessage(text, attachment) {
            if (this.loading || !this.contact || !this.contact.id || (!text.trim() && !attachment)) {
                return false;
            }

            const validationError = validateAttachment(attachment);

            if (validationError) {
                this.error = validationError;
                return false;
            }

            const contactId = this.contact.id;
            const type = this.type;
            const key = this.conversationKey;
            const version = this.conversationVersion;
            const payload = new FormData();
            payload.append('contact_id', contactId);
            payload.append('type', type);
            payload.append('text', text);

            if (attachment) {
                payload.append('attachment', attachment, attachment.name);
            }

            this.loading = true;
            this.loadmessage = true;
            this.error = '';
            this.failedMessage = null;
            this.$emit('stop-writing');

            try {
                // Deixe o navegador/axios definir o boundary do multipart.
                const response = await axios.post(`${CHAT_CONVERSATION_URL}/send`, payload, {
                    headers: { Accept: 'application/json' },
                });

                if (key === this.conversationKey && version === this.conversationVersion) {
                    if (this.attachment === attachment) {
                        this.attachment = null;
                    }

                    this.$emit('new', response.data);
                    this.scrollToBottom();
                }

                return true;
            } catch (error) {
                if (key === this.conversationKey && version === this.conversationVersion) {
                    this.error = requestErrorMessage(error);
                    // Conserva o texto mesmo se o MessageComposer atual limpar o campo ao emitir send.
                    this.failedMessage = { text, attachment, key };
                }

                return false;
            } finally {
                this.loading = false;
                this.loadmessage = false;
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const list = this.$refs.messageList;

                if (list) {
                    list.scrollTop = list.scrollHeight;
                }
            });
        },
    },
};
</script>

<style>
body .direct-chat-info .right > .direct-chat-text {
    background: #00a0a9;
    border-color: #00a0a9;
    color: #fff;
}
.direct-chat-info .right > .direct-chat-text:after,
.direct-chat-info .right > .direct-chat-text:before { border-left-color: #00a0a9; }
.direct-chat-info { display: block; margin-bottom: 2px; font-size: 14px; }
.conversation { background: aliceblue; width: 100%; }
</style>

<style scoped>
.chat-conversation-layout { height: 100%; min-height: 0; display: flex; flex-direction: column; }
.chat-message-list { flex: 1 1 0; min-height: 0; background: aliceblue; overflow-y: auto; overflow-x: hidden; }
.chat-composer-area { flex: 0 0 auto; min-width: 0; margin: 0; padding: 0; border: 0; }
</style>
